// Simple KV settings persistence over the 'settings' object store.
//
// PRD §6.6 promises optional user-supplied NCBI key + polite-pool mailto. PRD
// §9.6 promises opt-in LLM with model choice. All values stay on-device; sent
// only to their respective allowlisted hosts.

import { getDb } from './db';
import { DEFAULT_MODEL_ID, type LlmProvider } from '../llm/types';

export interface UserSettings {
  /** Polite-pool email for Crossref / OpenAlex. */
  mailto: string;
  /** Optional NCBI API key for PubMed (higher rate limits). */
  ncbiApiKey: string;
  /** First-run onboarding completed. */
  onboardingCompleted: boolean;
  /** Whether the editor has opted in to the LLM layer (PRD §9.6 lazy default). */
  llmEnabled: boolean;
  /**
   * Which LLM engine backs the optional enrichment pass.
   * `'on-device'` (default) = WebLLM/WebGPU, nothing leaves the device.
   * `'gemini'` = opt-in cloud (Google Gemini, BYOK) — manuscript excerpts
   * are sent to Google. See ADR-0004 amendment 2026-05-17.
   */
  llmProvider: LlmProvider;
  /**
   * Bring-your-own Google Gemini API key. Stored only here (IndexedDB),
   * sent only to generativelanguage.googleapis.com, and only when
   * `llmProvider === 'gemini'`. Never bundled or committed.
   */
  geminiApiKey: string;
  /** MLC model id (see SUPPORTED_MODELS in src/lib/llm/types.ts). */
  llmModelId: string;
  /**
   * Set once the on-device model has failed to load/run here because the
   * GPU can't support it (OOM / device-lost / WebGPU absent). Used to hide
   * the optional LLM gracefully and stop re-attempting a doomed ~GB download
   * (ADR-0003 amendment: editorial hardware often can't run in-browser LLM).
   * Cleared by the editor's explicit "Try anyway".
   */
  llmDeviceUnsupported: boolean;
}

const DEFAULTS: UserSettings = {
  mailto: '',
  ncbiApiKey: '',
  onboardingCompleted: false,
  llmEnabled: false,
  llmProvider: 'on-device',
  geminiApiKey: '',
  llmModelId: DEFAULT_MODEL_ID,
  llmDeviceUnsupported: false,
};

export async function getSetting<K extends keyof UserSettings>(key: K): Promise<UserSettings[K]> {
  const db = await getDb();
  const record = await db.get('settings', key as string);
  if (!record) return DEFAULTS[key];
  return record.value as UserSettings[K];
}

export async function setSetting<K extends keyof UserSettings>(
  key: K,
  value: UserSettings[K],
): Promise<void> {
  const db = await getDb();
  await db.put('settings', { key: key as string, value });
}

export async function getAllSettings(): Promise<UserSettings> {
  const db = await getDb();
  const merged: UserSettings = { ...DEFAULTS };
  for (const k of Object.keys(DEFAULTS) as Array<keyof UserSettings>) {
    const r = await db.get('settings', k as string);
    if (r) Object.assign(merged, { [k]: r.value });
  }
  return merged;
}
